/**
 * MatrizFasesBlock — visualiza la estructura de fases del regulador: la matriz
 * fase × grupos, la duración del ciclo y la fase en curso.
 *
 * Portado de `semaforo-ui` (vista de lab). Componente de solo lectura: recibe
 * los datos por props (del payload MQTT `status.matriz_fases` /
 * `status.tiempos_fase`). No depende de stores ni de MQTT.
 */
import type { MatrizFaseEntry } from '../../types/regulador-mqtt';
import { UNE_COLORS, UNE_COLOR_FALLBACK } from '../../data/une-colors';

interface MatrizFasesBlockProps {
  matriz: MatrizFaseEntry[];
  faseActual: string | number | null;
  tiempos?: Record<string, number>;
}

export function MatrizFasesBlock({ matriz, faseActual, tiempos }: MatrizFasesBlockProps) {
  const faseStr = faseActual != null ? String(faseActual) : '';

  return (
    <div className="px-4 py-3 border-b border-gray-800 space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="text-[10px] text-gray-500 uppercase tracking-wider">
          Estructura de fases · {matriz.length}
          {tiempos && (() => {
            const total = Object.values(tiempos).reduce((s, v) => s + (Number.isFinite(v) ? v : 0), 0);
            if (total <= 0) return null;
            const fmt = total < 60
              ? `${Math.round(total)} s`
              : `${Math.floor(total / 60)} min ${Math.round(total % 60)} s`;
            return <span className="text-gray-600 normal-case"> · ciclo {fmt}</span>;
          })()}
        </div>
        {faseStr && (
          <div className="text-[10px] text-amber-300 font-mono font-bold">
            ▸ Fase actual: {faseStr}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {matriz.map(f => {
          const isActive = f.fase === faseStr || String(f.indice) === faseStr;
          const dur = tiempos?.[f.fase];
          const durStr = typeof dur === 'number'
            ? `${dur < 10 ? dur.toFixed(1) : Math.round(dur)}s`
            : null;
          return (
            <div
              key={`${f.indice}-${f.fase}`}
              className={`flex flex-col items-center gap-0.5 px-1.5 py-1.5 rounded border-2 transition-all ${
                isActive
                  ? 'border-amber-400 bg-amber-900/40 ring-2 ring-amber-400/60 shadow-[0_0_8px_rgba(251,191,36,0.4)] scale-105'
                  : 'border-gray-800 bg-gray-900/60'
              }`}
              title={`Fase ${f.fase} · índice ${f.indice}${durStr ? ` · ${durStr}` : ''}${isActive ? ' (en curso)' : ''}`}
            >
              <span className={`text-[10px] font-mono ${isActive ? 'text-amber-200 font-bold' : 'text-gray-500'}`}>
                {f.fase}
              </span>
              <div className="flex gap-0.5">
                {f.grupos.map(g => (
                  <div
                    key={g.grupo}
                    className={isActive ? 'w-2.5 h-2.5 rounded-full' : 'w-2 h-2 rounded-full'}
                    style={{
                      backgroundColor: UNE_COLORS[g.estado_une] || UNE_COLOR_FALLBACK,
                      boxShadow: isActive
                        ? `0 0 6px ${UNE_COLORS[g.estado_une] || UNE_COLOR_FALLBACK}`
                        : undefined,
                    }}
                    title={`G${g.grupo} ${g.label}`}
                  />
                ))}
              </div>
              {durStr && (
                <span className={`text-[9px] font-mono mt-0.5 ${isActive ? 'text-amber-200' : 'text-gray-600'}`}>
                  {durStr}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
